import { useEffect, useState } from "react";

/**
 * Fractional scroll positions and display scaling mean the last pixel of a
 * scroll is never reached exactly. Small enough that a diff row cannot hide in
 * it.
 */
const SCROLL_END_TOLERANCE = 4;

/**
 * True once the element has been scrolled to its end, or never had anything to
 * scroll. It latches, so the approval bar cannot expand, push the end out of
 * reach and collapse itself again.
 *
 * Takes the node rather than a ref so that it re-measures whenever the node
 * appears: inside a dialog the content mounts a pass later than the effect that
 * would have read a ref, which left the gate watching nothing.
 */
export function useReadToBottom(
    element: HTMLElement | null,
    resetKey: string | undefined,
    isReady: boolean,
) {
    const [hasRead, setHasRead] = useState(false);

    useEffect(() => {
        setHasRead(false);
    }, [resetKey]);

    useEffect(() => {
        if (!element || !isReady) return;

        const check = () => {
            // A hidden or not yet laid out element measures zero everywhere,
            // which would otherwise count as read.
            if (element.clientHeight === 0) return;
            // See SCROLL_END_TOLERANCE.x
            const remaining =
                element.scrollHeight - element.scrollTop - element.clientHeight;
            if (remaining <= SCROLL_END_TOLERANCE) setHasRead(true);
        };

        // A frame late, so the first measurement sees a laid-out diff.
        const frame = requestAnimationFrame(check);
        element.addEventListener("scroll", check, { passive: true });
        // The diff arrives after this runs, and can grow as sections expand.
        const observer = new ResizeObserver(check);
        observer.observe(element);
        if (element.firstElementChild)
            observer.observe(element.firstElementChild);

        return () => {
            cancelAnimationFrame(frame);
            element.removeEventListener("scroll", check);
            observer.disconnect();
        };
    }, [element, resetKey, isReady]);

    return hasRead;
}
